<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Catálogos por Sección
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Asigna catálogos a cada sección (puedes agregar el mismo catálogo múltiples veces)
      </p>
    </div>

    <!-- Selector de Sección -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
      <div class="p-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Seleccionar Sección
        </label>
        <select
          v-model="seccionSeleccionada"
          @change="cargarCatalogosDeSeccion"
          class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
        >
          <option :value="null">Seleccione una sección...</option>
          <option v-for="seccion in secciones" :key="seccion.seccionId" :value="seccion">
            {{ seccion.nombreSeccion }}
          </option>
        </select>
      </div>
    </div>

    <!-- Lista de Catálogos Asignados -->
    <div v-if="seccionSeleccionada" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Catálogos en: {{ seccionSeleccionada.nombreSeccion }}
          </h2>
          <button
            @click="showModal = true"
            class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Agregar Catálogo
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
        </div>

        <div v-else-if="catalogosAsignados.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
          No hay catálogos asignados a esta sección
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="(asignacion, index) in catalogosAsignados"
            :key="asignacion.seccionCatalogoId"
            class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <div class="flex-1">
              <div class="flex items-center gap-3">
                <span class="text-lg font-bold text-violet-400 dark:text-violet-500">#{{ index + 1 }}</span>
                <div>
                  <div class="font-semibold text-gray-900 dark:text-white text-base">
                    {{ asignacion.nombreMostrar || asignacion.catalogo?.nombreCatalogo || 'Sin nombre' }}
                  </div>
                  <div v-if="asignacion.nombreMostrar" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Catálogo: {{ asignacion.catalogo?.nombreCatalogo }}
                  </div>
                  <div v-if="asignacion.descripcion || asignacion.catalogo?.descripcion" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {{ asignacion.descripcion || asignacion.catalogo?.descripcion }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                      Orden: {{ asignacion.orden }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              @click="eliminarAsignacion(asignacion)"
              class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition"
              title="Eliminar catálogo de esta sección"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para Agregar Catálogo -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Agregar Catálogo a Sección
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Catálogo *
            </label>
            <select
              v-model="catalogoAgregar"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option :value="null">Seleccione un catálogo...</option>
              <option v-for="catalogo in catalogos" :key="catalogo.catalogoId" :value="catalogo">
                {{ catalogo.nombreCatalogo }}
              </option>
            </select>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Puedes agregar el mismo catálogo múltiples veces
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre a Mostrar
            </label>
            <input
              v-model="nombreMostrarAgregar"
              type="text"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="Ej: IMPRESIÓN 1"
            />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Alias personalizado para diferenciar catálogos repetidos
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Orden
            </label>
            <input
              v-model.number="ordenAgregar"
              type="number"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              placeholder="0"
            />
          </div>
        </div>

        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            @click="closeModal"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancelar
          </button>
          <button
            @click="agregarCatalogo"
            :disabled="!catalogoAgregar || loading"
            class="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
          >
            {{ loading ? 'Agregando...' : 'Agregar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCatalogosNew } from '../../composables/useCatalogosNew'

const {
  loading,
  secciones,
  catalogos,
  fetchSecciones,
  fetchCatalogos,
  obtenerCatalogosDeSeccion,
  asignarCatalogoASeccion,
  desasignarCatalogoDeSeccion
} = useCatalogosNew()

const seccionSeleccionada = ref(null)
const catalogosAsignados = ref([])
const showModal = ref(false)
const catalogoAgregar = ref(null)
const nombreMostrarAgregar = ref('')
const ordenAgregar = ref(0)

onMounted(async () => {
  await Promise.all([
    fetchSecciones(),
    fetchCatalogos()
  ])
})

const cargarCatalogosDeSeccion = async () => {
  if (!seccionSeleccionada.value) {
    catalogosAsignados.value = []
    return
  }

  try {
    catalogosAsignados.value = await obtenerCatalogosDeSeccion(seccionSeleccionada.value.seccionId)
  } catch (error) {
    console.error('Error al cargar catálogos de sección:', error)
    catalogosAsignados.value = []
  }
}

const agregarCatalogo = async () => {
  if (!catalogoAgregar.value || !seccionSeleccionada.value) return

  const data = {
    seccionId: seccionSeleccionada.value.seccionId,
    catalogoId: catalogoAgregar.value.catalogoId,
    nombreMostrar: nombreMostrarAgregar.value || null,
    orden: ordenAgregar.value || catalogosAsignados.value.length
  }

  console.log('📤 Enviando datos para agregar catálogo:', data)

  try {
    const result = await asignarCatalogoASeccion(data)
    console.log('✅ Catálogo agregado exitosamente:', result)
    await cargarCatalogosDeSeccion()
    closeModal()
  } catch (error) {
    console.error('❌ Error al agregar catálogo:', error)
    console.error('Error response:', error.response?.data)
  }
}

const eliminarAsignacion = async (asignacion) => {
  if (!confirm(`¿Eliminar el catálogo "${asignacion.catalogo?.nombreCatalogo}" de esta sección?`)) return

  try {
    await desasignarCatalogoDeSeccion(asignacion.seccionCatalogoId)
    await cargarCatalogosDeSeccion()
  } catch (error) {
    console.error('Error al eliminar asignación:', error)
  }
}

const closeModal = () => {
  showModal.value = false
  catalogoAgregar.value = null
  nombreMostrarAgregar.value = ''
  ordenAgregar.value = 0
}
</script>
